import { ChangeDetectionStrategy, Component, ElementRef, HostListener, Input, OnDestroy, OnInit, Optional } from '@angular/core';

import { BehaviorSubject, Subject, filter, take, takeUntil, zip } from 'rxjs';

import { Memoize } from '@core/decorators';
import { EnvironmentService, GridService } from '@core/services';

import { Column, Columns } from '#types/grid';

import { TableComponent } from '../table/table.component';

@Component({
  selector: 'tr[z-grid-table-body-cell]',
  templateUrl: './table-body-cell.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TableBodyCellComponent implements OnInit, OnDestroy {
  destroy$ = new Subject<void>();

  @Input() gridId: string;
  @Input() columns: Columns;
  @Input() data: any;
  @Input() expanded: any;
  @Input() rowgroup: any;
  @Input() rowspan: any;
  @Input() index: any;
  @Input() editing: any;
  @Input() enableRowEditing: boolean;
  @Input() rowEditingEnableConditionColumn: string;
  @Input() rowEditingEnableConditionValue: string;

  rowEditingActive$ = new BehaviorSubject<boolean>(false);

  detectChanges$ = new Subject<void>();

  constructor(
    public environmentService: EnvironmentService,
    public gridService: GridService,
    @Optional() private tableComponent: TableComponent,
    public elementRef: ElementRef
  ) {}

  ngOnInit() {
    if (!this.tableComponent) {
      return;
    }

    this.tableComponent.rowEditInit$
      .pipe(
        filter(({ index }) => index === this.index),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.rowEditingActive$.next(true);
      });

    this.tableComponent.rowEditSave$
      .pipe(
        filter(({ index }) => index === this.index),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.rowEditingActive$.next(false);
      });

    this.tableComponent.rowEditCancel$
      .pipe(
        filter(({ index }) => index === this.index),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.rowEditingActive$.next(false);
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.unsubscribe();
  }

  trackBy(_index: number, column: Column) {
    return column.key;
  }

  @HostListener('keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    if (event.key?.toLowerCase() === 'c' && event.ctrlKey) {
      zip(this.gridService.selectedColumns$(this.gridId), this.gridService.selectedRows$(this.gridId))
        .pipe(take(1))
        .subscribe(([columns, rows]) => {
          this.gridService.copyRowToClipboard(columns, rows, !(event.shiftKey || event.altKey));
        });
    }
  }

  @Memoize()
  checkRowEditingCondition(column: string, matchValue: string, columnValue: string) {
    if (!column || !matchValue) {
      return true;
    }

    if (!columnValue || columnValue !== matchValue) {
      return false;
    } else {
      return true;
    }
  }
}
